import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Sidebar from "../component/Sidebar";
import Pagination from "./Pagination";
import toast from "react-hot-toast";
import CollapsibleHeader from "../component/CollapsibleHeader";
import { FaArrowLeft } from "react-icons/fa";

const formatOTDisplay = (otValue) => {
  const val = parseFloat(otValue);
  if (!val || val <= 0) return "0h 0m";

  const totalMinutes = Math.round(val * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  return `${h}h ${m}m`;
};

const EMP_COLORS = [
  "bg-dorika-blueLight",
  "bg-dorika-green/20",
  "bg-dorika-orange/20",
];

const getRowColor = (index) => EMP_COLORS[index % EMP_COLORS.length];

const getAttendanceTextColor = (val) => {
  switch (val) {
    case "P":
      return "text-green-600";
    case "P(L)":
      return "text-orange-500";
    case "A":
      return "text-red-600";
    case "SL":
    case "SL(OFF)":
      return "text-purple-600";
    case "CL":
    case "CL(OFF)":
      return "text-blue-600";
    case "OFF":
      return "text-black";
    default:
      return "";
  }
};

const AttendanceController = () => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [employees, setEmployees] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [designations, setDesignations] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState("ALL");
  const [departments, setDepartments] = useState(["ALL"]);
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const tableContainerRef = useRef(null);

  const daysInMonth = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const totalDays = new Date(year, month, 0).getDate();
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  }, [selectedMonth]);

  const dayHeaders = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    return daysInMonth.map((day) => ({
      day,
      label: new Date(year, month - 1, day).toLocaleDateString("en-US", { weekday: "short" }),
    }));
  }, [daysInMonth, selectedMonth]);

  useEffect(() => {
    const fetchMasterData = async () => {
      setLoading(true);
      try {
        const [employeeRes, departmentRes] = await Promise.all([
          axios.get("/api/employees"),
          axios.get("/api/departments"),
        ]);

        setEmployees(Array.isArray(employeeRes.data) ? employeeRes.data : []);
        setDepartments(["ALL", ...(Array.isArray(departmentRes.data) ? departmentRes.data.map((d) => d.deptName) : [])]);
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();
  }, []);

  useEffect(() => {
    let filteredEmployees = employees;

    if (selectedDepartment !== "ALL") {
      filteredEmployees = employees.filter((emp) => emp.departmentName === selectedDepartment);
    }

    setDesignations([
      "ALL",
      ...new Set(filteredEmployees.map((emp) => emp.designationName).filter(Boolean)),
    ]);
  }, [employees, selectedDepartment]);

  useEffect(() => {
    const fetchAttendance = async () => {
      const [year, month] = selectedMonth.split("-");
      try {
        const res = await axios.get(`/api/attendance/history?month=${Number(month)}&year=${Number(year)}`);
        const map = {};

        (Array.isArray(res.data) ? res.data : []).forEach((doc) => {
          const dayMap = {};

          (doc.records || []).forEach((record) => {
            const day = new Date(record.date).getDate();

            let status = "";
            if (record.status === "Present" && record.isLate) status = "P(L)";
            else if (record.status === "Present") status = "P";
            else if (record.status === "Absent") status = "A";
            else status = record.status;

            dayMap[day] = {
              status,
              shiftCode: record.shiftCode || "-",
              shiftStartTime: record.shiftStartTime || "-",
              shiftEndTime: record.shiftEndTime || "-",
            };
          });

          map[doc.employeeUserId] = {
            days: dayMap,
            totalPresent: doc.totalPresent || 0,
            totalAbsent: doc.totalAbsent || 0,
            totalOff: doc.totalOff || 0,
            totalLeave: doc.totalLeave || 0,
            totalOTHours: doc.totalOTHours || 0,
          };
        });

        setAttendanceMap(map);
      } catch (err) {
        setAttendanceMap({});
      }
    };

    fetchAttendance();
  }, [selectedMonth]);

  const filteredEmployees = useMemo(() => {
    const searchValue = searchTerm.trim().toUpperCase();

    return employees.filter((emp) => {
      const departmentMatch =
        selectedDepartment === "ALL" || emp.departmentName === selectedDepartment;
      const designationMatch =
        selectedDesignation === "ALL" || emp.designationName === selectedDesignation;
      const fullName = `${emp.firstName || ""} ${emp.middleName || ""} ${emp.lastName || ""}`.toUpperCase();
      const empId = String(emp.employeeID || "").toUpperCase();
      const userId = String(emp.employeeUserId || "").toUpperCase();
      const searchMatch =
        !searchValue ||
        fullName.includes(searchValue) ||
        empId.includes(searchValue) ||
        userId.includes(searchValue);

      return departmentMatch && designationMatch && searchMatch;
    });
  }, [employees, searchTerm, selectedDepartment, selectedDesignation]);

  const startIndex = perPage === "all" ? 0 : (currentPage - 1) * perPage;
  const paginatedEmployees =
    perPage === "all"
      ? filteredEmployees
      : filteredEmployees.slice(startIndex, startIndex + perPage);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({
        left: direction === "left" ? -400 : 400,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden md:flex-row">
      <Sidebar />

      <div className="page-panel">
        <div className="page-panel rounded-md bg-white p-3 shadow-md">
          <div className="page-sticky-header mb-3 bg-white pb-2">
            <div className="page-title-bar mb-3 flex items-center justify-between">
              <h2 className="text-xl font-bold text-dorika-blue">Attendance Controller</h2>
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex h-8 w-[110px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white shadow transition hover:bg-blue-800"
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
            </div>

            <CollapsibleHeader title="Attendance Controller Filters" contentClassName="mt-3">
              <div className="page-title-bar">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                  <div className="field-stack-mobile">
                    <label>Search</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                      placeholder="Search Name / Emp ID / User ID"
                      className="h-8 rounded border border-dorika-blue px-3 py-1 text-sm focus:outline-none"
                    />
                  </div>

                  <div className="field-stack-mobile">
                    <label>Month</label>
                    <input
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="h-8 rounded border border-dorika-blue px-3 py-1 text-sm focus:outline-none"
                    />
                  </div>

                  <div className="field-stack-mobile">
                    <label>Department</label>
                    <select
                      value={selectedDepartment}
                      onChange={(e) => {
                        setSelectedDepartment(e.target.value);
                        setSelectedDesignation("ALL");
                      }}
                      className="h-8 rounded border border-dorika-blue px-3 py-1 text-sm"
                    >
                      {departments.map((department) => (
                        <option key={department}>{department}</option>
                      ))}
                    </select>
                  </div>

                  <div className="field-stack-mobile">
                    <label>Designation</label>
                    <select
                      value={selectedDesignation}
                      onChange={(e) => setSelectedDesignation(e.target.value)}
                      className="h-8 rounded border border-dorika-blue px-3 py-1 text-sm"
                    >
                      {designations.map((designation) => (
                        <option key={designation}>{designation}</option>
                      ))}
                    </select>
                  </div>

                  <div className="field-stack-mobile">
                    <label>Show</label>
                    <select
                      value={perPage}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPerPage(value === "all" ? "all" : Number(value));
                        setCurrentPage(1);
                      }}
                      className="h-8 rounded border border-dorika-blue bg-white px-3 py-1 text-sm font-semibold text-dorika-blue"
                    >
                      <option value={8}>8</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value="all">ALL</option>
                    </select>
                  </div>
                </div>
              </div>
            </CollapsibleHeader>
          </div>

          <div className="relative flex min-h-0 flex-1 flex-col">
           <button 
                type="button"
                onClick={() => scrollTable('left')}
                className="fixed left-[280px] top-1/2 -translate-y-1/2 z-50  text-green-500 p-4 rounded-full  transition-all active:scale-90 hidden md:block"
                style={{ marginLeft: '10px' }} // Ensures it doesn't touch the sidebar
              >
                <span className="text-3xl font-bold">❮</span>
              </button>

              {/* Right Fixed Gray Arrow */}
              <button 
                type="button"
                onClick={() => scrollTable('right')}
                className="fixed right-6 top-1/2 -translate-y-1/2 z-50  text-green-500 p-4 rounded-full  transition-all active:scale-90 hidden md:block"
              >
                <span className="text-3xl font-bold">❯</span>
              </button>


            <div ref={tableContainerRef} className="table-scroll-area w-full scroll-smooth border shadow">
              <table className="table-fixed-header w-full border-collapse border-dorika-blue text-[10px] sm:text-xs">
                <thead className="bg-dorika-blue text-white">
                  <tr>
                    <th className="border border-dorika-blue px-2">SL</th>
                    <th className="border border-dorika-blue px-2">Emp ID</th>
                    <th className="border border-dorika-blue px-2">User ID</th>
                    <th className="border border-dorika-blue px-2">Name</th>
                    <th className="border border-dorika-blue px-2">Department</th>
                    <th className="border border-dorika-blue px-2">Designation</th>
                    {dayHeaders.map(({ day, label }) => (
                      <th
                        key={day}
                        className="border border-dorika-blue text-center whitespace-nowrap"
                        style={{ width: "32px", minWidth: "32px", maxWidth: "32px" }}
                      >
                        <div className="flex flex-col leading-tight">
                          <span>{day}</span>
                          <span className="text-[9px] font-normal uppercase">{label}</span>
                        </div>
                      </th>
                    ))}
                    <th className="border border-dorika-blue bg-orange-500 px-2 text-center">TP</th>
                    <th className="border border-dorika-blue bg-orange-500 px-2 text-center">TA</th>
                    <th className="border border-dorika-blue bg-orange-500 px-2 text-center">TO</th>
                    <th className="border border-dorika-blue bg-orange-500 px-2 text-center">TL</th>
                    <th className="border border-dorika-blue bg-orange-500 px-2 text-center">TOT</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={dayHeaders.length + 11} className="border px-3 py-10 text-center text-gray-500">
                        Loading employees...
                      </td>
                    </tr>
                  ) : (
                    paginatedEmployees.map((emp, index) => (
                      <tr key={emp.employeeUserId} className={getRowColor(startIndex + index)}>
                        <td className="border border-dorika-blue px-2">
                          {perPage === "all" ? index + 1 : startIndex + index + 1}
                        </td>
                        <td className="border border-dorika-blue px-2">{emp.employeeID}</td>
                        <td className="border border-dorika-blue px-2">{emp.employeeUserId}</td>
                        <td className="border border-dorika-blue px-2 font-medium">
                          {emp.firstName} {emp.lastName}
                        </td>
                        <td className="border border-dorika-blue px-2">{emp.departmentName}</td>
                        <td className="border border-dorika-blue px-2">{emp.designationName}</td>

                        {daysInMonth.map((day) => {
                          const valObj = attendanceMap?.[emp.employeeUserId]?.days?.[day];
                          const currentStatus = valObj?.status || "";
                          const editableStatuses = ["P", "P(L)", "A"];
                          const isEditable = editableStatuses.includes(currentStatus);

                          return (
                            <td
                              key={day}
                              className={`border border-dorika-blue text-center font-bold whitespace-nowrap overflow-hidden text-ellipsis ${getAttendanceTextColor(currentStatus)}`}
                              style={{
                                width: "32px",
                                minWidth: "32px",
                                maxWidth: "32px",
                                cursor: isEditable ? "pointer" : "not-allowed",
                              }}
                              title={
                                valObj
                                  ? `Shift: ${valObj.shiftCode}\nStart: ${valObj.shiftStartTime}\nEnd: ${valObj.shiftEndTime}`
                                  : ""
                              }
                            >
                              {isEditable ? (
                                <select
                                  value={currentStatus}
                                  onChange={async (e) => {
                                    const newStatus = e.target.value;

                                    toast(
                                      (t) => (
                                        <div className="flex min-w-[220px] flex-col gap-3">
                                          <div className="flex items-center gap-3">
                                            <div className="h-10 w-1.5 rounded-full bg-green-500"></div>
                                            <p className="text-sm font-semibold text-gray-800">
                                              Set attendance to{" "}
                                              <span className="font-bold text-green-600 underline">{newStatus}</span>?
                                            </p>
                                          </div>

                                          <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-3">
                                            <button
                                              className="rounded-md bg-red-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-red-600 transition-all hover:bg-red-600 hover:text-white"
                                              onClick={() => toast.dismiss(t.id)}
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              className="rounded-md bg-green-600 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-md shadow-green-100 transition-all active:scale-95 hover:bg-green-700"
                                              onClick={async () => {
                                                toast.dismiss(t.id);

                                                const payload = {
                                                  employeeUserId: emp.employeeUserId,
                                                  date: `${selectedMonth}-${String(day).padStart(2, "0")}`,
                                                  status:
                                                    newStatus === "P" || newStatus === "P(L)"
                                                      ? "Present"
                                                      : "Absent",
                                                  isLate: newStatus === "P(L)",
                                                };

                                                try {
                                                  await axios.put("/api/attendance/update", payload);
                                                  const [year, month] = selectedMonth.split("-");
                                                  const refreshRes = await axios.get(
                                                    `/api/attendance/history?month=${Number(month)}&year=${Number(year)}`
                                                  );
                                                  const map = {};

                                                  (Array.isArray(refreshRes.data) ? refreshRes.data : []).forEach((doc) => {
                                                    const dayMap = {};

                                                    (doc.records || []).forEach((record) => {
                                                      const recordDay = new Date(record.date).getDate();

                                                      let statusValue = "";
                                                      if (record.status === "Present" && record.isLate) statusValue = "P(L)";
                                                      else if (record.status === "Present") statusValue = "P";
                                                      else if (record.status === "Absent") statusValue = "A";
                                                      else statusValue = record.status;

                                                      dayMap[recordDay] = {
                                                        status: statusValue,
                                                        shiftCode: record.shiftCode || "-",
                                                        shiftStartTime: record.shiftStartTime || "-",
                                                        shiftEndTime: record.shiftEndTime || "-",
                                                      };
                                                    });

                                                    map[doc.employeeUserId] = {
                                                      days: dayMap,
                                                      totalPresent: doc.totalPresent || 0,
                                                      totalAbsent: doc.totalAbsent || 0,
                                                      totalOff: doc.totalOff || 0,
                                                      totalLeave: doc.totalLeave || 0,
                                                      totalOTHours: doc.totalOTHours || 0,
                                                    };
                                                  });

                                                  setAttendanceMap(map);
                                                } catch (err) {
                                                  toast.error("Update failed!");
                                                }
                                              }}
                                            >
                                              Confirm
                                            </button>
                                          </div>
                                        </div>
                                      ),
                                      {
                                        duration: 6000,
                                        position: "top-center",
                                        style: {
                                          padding: "16px",
                                          borderRadius: "12px",
                                          border: "1px solid #f1f5f9",
                                          background: "#ffffff",
                                        },
                                      }
                                    );
                                  }}
                                  className={`w-full cursor-pointer bg-transparent text-center font-bold ${getAttendanceTextColor(currentStatus)}`}
                                >
                                  <option value="P">P</option>
                                  <option value="P(L)">P(L)</option>
                                  <option value="A">A</option>
                                </select>
                              ) : (
                                currentStatus
                              )}
                            </td>
                          );
                        })}

                        <td className="border border-dorika-blue text-center font-bold text-green-700">
                          {attendanceMap?.[emp.employeeUserId]?.totalPresent || 0}
                        </td>
                        <td className="border border-dorika-blue text-center font-bold text-red-600">
                          {attendanceMap?.[emp.employeeUserId]?.totalAbsent || 0}
                        </td>
                        <td className="border border-dorika-blue text-center font-bold text-gray-600">
                          {attendanceMap?.[emp.employeeUserId]?.totalOff || 0}
                        </td>
                        <td className="border border-dorika-blue text-center font-bold text-orange-600">
                          {attendanceMap?.[emp.employeeUserId]?.totalLeave || 0}
                        </td>
                        <td className="border border-dorika-blue text-center font-bold text-red-600">
                          {formatOTDisplay(attendanceMap?.[emp.employeeUserId]?.totalOTHours || 0)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {perPage !== "all" && (
              <Pagination
                total={filteredEmployees.length}
                perPage={perPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceController;
